import React, {useState, useRef, useEffect} from 'react';
import { createPortal } from 'react-dom';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  GestureResponderEvent,
} from 'react-native';

type MenuOption = {
  label: string;
  onClick: () => void;
};

type ContextMenuProps = {
  menuOptions: MenuOption[];
  children: React.ReactNode;
  style?: any;
};

const ContextMenu: React.FC<ContextMenuProps> = ({menuOptions, children, style}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{x: number; y: number}>({
    x: 0,
    y: 0,
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Inject transition animations style once on mount
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const styleId = 'context-menu-keyframes';
      if (!document.getElementById(styleId)) {
        const styleTag = document.createElement('style');
        styleTag.id = styleId;
        styleTag.innerHTML = `
          @keyframes contextMenuFadeIn {
            from {
              opacity: 0;
              transform: scale(0.96) translateY(-4px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `;
        document.head.appendChild(styleTag);
      }
    }
  }, []);

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setMenuPosition({x: event.clientX, y: event.clientY});
    setMenuVisible(true);
  };

  const handleLongPress = (event: GestureResponderEvent) => {
    const {pageX, pageY} = event.nativeEvent;
    setMenuPosition({x: pageX, y: pageY});
    setMenuVisible(true);
  };

  const handleOptionClick = (option: string) => {
    console.log('Selected option:', option);
    setMenuVisible(false);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setMenuVisible(false);
    }
  };

  useEffect(() => {
    if (menuVisible) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [menuVisible]);

  // Prevent the menu from going out of bounds of the window viewport
  const adjustedPosition = (menuWidth: number, menuHeight: number) => {
    let x = menuPosition.x;
    let y = menuPosition.y;

    if (typeof window !== 'undefined') {
      // Adjust if overflows right side of viewport
      if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 10;
      }
      // Adjust if overflows left side of viewport
      if (x < 10) {
        x = 10;
      }
      // Adjust if overflows bottom of viewport
      if (y + menuHeight > window.innerHeight) {
        y = window.innerHeight - menuHeight - 10;
      }
      // Adjust if overflows top of viewport
      if (y < 10) {
        y = 10;
      }
    }

    return {x, y};
  };

  const flexVal = style && typeof style === 'object' && !Array.isArray(style) ? style.flex : undefined;

  return (
    <div
      ref={wrapperRef}
      onContextMenu={event => handleContextMenu(event)}
      style={{position: 'relative', ...style}}>
      <View onLongPress={handleLongPress} style={{position: 'relative', flex: flexVal}}>
        {children}
      </View>

      {menuVisible && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          style={{
            ...menuWebStyle,
            top: adjustedPosition(150, 90).y,
            left: adjustedPosition(150, 90).x,
          }}>
          {menuOptions.map((option, index) => {
            const isHovered = hoveredIndex === index;
            const isLast = index === menuOptions.length - 1;
            return (
              <TouchableOpacity
                key={index}
                onHoverIn={() => setHoveredIndex(index)}
                onHoverOut={() => setHoveredIndex(null)}
                style={[
                  styles.optionButton,
                  isHovered && {backgroundColor: '#EEF9F7'},
                  !isLast && {
                    borderBottomWidth: 1,
                    borderBottomColor: 'rgba(107, 88, 77, 0.06)',
                  },
                ]}
                onPress={() => {
                  option.onClick();
                  setMenuVisible(false);
                }}>
                <Text
                  style={[
                    styles.menuOptionText,
                    isHovered && {color: '#3DAB9E', fontWeight: '600'},
                  ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const menuWebStyle: React.CSSProperties = {
  position: 'fixed',
  backgroundColor: '#FFFFFF',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(107, 88, 77, 0.12), 0 2px 4px rgba(107, 88, 77, 0.04)',
  border: '1px solid rgba(107, 88, 77, 0.08)',
  zIndex: 99999,
  width: 150,
  overflow: 'hidden',
  paddingTop: 4,
  paddingBottom: 4,
  animation: 'contextMenuFadeIn 0.12s cubic-bezier(0.16, 1, 0.3, 1)',
  transformOrigin: 'top left',
};

const styles = StyleSheet.create({
  optionButton: {
    paddingVertical: 9,
    paddingHorizontal: 14,
    transitionProperty: 'all',
    transitionDuration: '0.15s',
  },
  menuOptionText: {
    fontSize: 13,
    color: '#4D3F37',
    fontWeight: '500',
  },
});

export default ContextMenu;
